import React, { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { observer } from 'mobx-react';
import { Box, Divider } from '@material-ui/core';
import Routes from 'routes';
import { getSimilarTransactionsData } from 'models/detached-actions/report';
import { useReportContext } from 'contexts/report/ReportContext';
import DefaultLocales from 'globals/locales/default';
import reportLocales from 'globals/locales/report';
import { wait } from 'globals/utils';
import { ModalState, TransactionType } from 'globals/types';
import FsButton from 'ui-elements/buttons/FsButton';
import FsSubtitle from 'ui-elements/typography/FsSubtitle';
import FsBodySmall from 'ui-elements/typography/FsBodySmall';
import { ChangeCategoryLocationStateProps } from 'ui-elements/tables/FsExpenditureTable/FsExpenditureTableTransactionRow/FsExpenditureTableTransactionRow';
import { ExpenditureTableCategoryRowDataProps } from 'ui-elements/tables/FsExpenditureTable/FsExpenditureTableCategoryRow/FsExpenditureTableCategoryRow';
import FsBodySmallBold from 'ui-elements/typography/FsBodySmallBold';
import FsCheckboxWithLabel from 'ui-elements/form-elements/FsCheckboxWithLabel';
import FsDialogTitle from 'ui-elements/modals/FsDialogTitle';
import FsBody from 'ui-elements/typography/FsBody';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import SyncIcon from '@material-ui/icons/Sync';
import FsGridContainer from 'ui-elements/grid/FsGridContainer';
import FsGridItem from 'ui-elements/grid/FsGridItem';
import {
  CustomDialog,
  DialogActions,
  DialogContent,
} from '../ReportModals.style-elements';
import CategoryList from './CategoryList';
import { CategoryListItem } from './CategoryList/CategoryList';
import {
  areSubCategoriesExist,
  getKeyWords,
} from './ReportChangeCategoryModal.utils';
import TagList from './TagList';
import { ExportFileType } from '../ReportExportModal/SingleAccountExportSelector/SingleAccountExportSelector';
import TransactionDetails from './TransactionDetails';
import HelperAlert from './HelperAlert';

export interface ReportChangeCategoryModalLocationStateProps {
  success?: ModalState;
  name?: string;
  newCategory?: string | null;
  withSimilar?: boolean;
  numberOfSimilar?: number;
}

const { saveChanges: saveChangesLabel, cancel: cancelLabel } = DefaultLocales;
const {
  transactionLabel,
  dialogTitle,
  tagTitle,
  categorySubTitle,
  tagSubTitle,
  mainCategory,
  subCategory,
  similarSubTitle,
  similarCheckboxLabel,
} = reportLocales.changeCategoryModal;

const ReportChangeCategoryModal: React.FC<RouteComponentProps> = ({
  history,
  location,
}) => {
  const modalState = location.state as ChangeCategoryLocationStateProps;
  const transaction = modalState?.transaction;
  const transactionType = modalState?.transaction?.type;
  const keyWords = transaction?.name ? getKeyWords(transaction?.name) : [];
  const {
    report: {
      bankAccountsFinancialStats: { expenditureCategories, incomeCategories },
    },
  } = useReportContext();

  const getCategoriesByType = (
    type: TransactionType | undefined
  ): ExpenditureTableCategoryRowDataProps[] => {
    switch (type) {
      case TransactionType.EXPENDITURE:
        return expenditureCategories();
      case TransactionType.INCOME:
        return incomeCategories();
      default:
        return [];
    }
  };

  const categories = getCategoriesByType(transactionType);
  const areCategories = !!categories.length;
  const [subCategories, setSubCategories] = useState<CategoryListItem[]>([]);
  const areSubcategories = !!subCategories.length;
  const [currentSelection, setCurrentSelection] = useState<string | null>(null);
  const [currentSelectedCategory, setCurrentSelectedCategory] = useState<
    string | null
  >(null);
  const [currentSelectedSubCategory, setCurrentSelectedSubCategory] = useState<
    string | null
  >(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [withSimilar, setWithSimilar] = useState<boolean>(false);
  const [pending, setPending] = useState<boolean>(false);
  const [numberOfSimilar, setNumberOfSimilar] = useState<number>();

  const handleClose = (): void => {
    history.push({ pathname: Routes.REPORT });
  };

  const redirectToModalFail = (): void => {
    const pathname = Routes.REPORT_MODAL_CHANGE_CATEGORY_FAIL;
    const state: ReportChangeCategoryModalLocationStateProps = {
      success: ModalState.FAIL,
    };
    history.push({
      pathname,
      state,
    });
  };

  const redirectToModalSuccess = (): void => {
    const pathname = Routes.REPORT_MODAL_CHANGE_CATEGORY_SUCCESS;
    const state: ReportChangeCategoryModalLocationStateProps = {
      success: ModalState.SUCCESS,
      name: transaction?.name,
      newCategory: currentSelection,
      withSimilar,
      numberOfSimilar,
    };
    history.push({
      pathname,
      state,
    });
  };

  const handleSend = (): boolean => {
    // TODO: API | bind with proper endpoint
    const payload = {
      transactionId: transaction?.id,
      transactionType: transaction?.type,
      newCategory: currentSelection,
      selectedTags,
      withSimilar,
    };
    // eslint-disable-next-line no-console
    console.log('payload', JSON.stringify(payload, null, 2));
    return Math.random() < 0.5;
  };

  const handleSaveAndClose = async (): Promise<void> => {
    const sendStatus = handleSend();
    setPending(true);
    await wait(300);
    setPending(false);
    handleClose();
    if (sendStatus) {
      redirectToModalSuccess();
      return;
    }
    redirectToModalFail();
  };

  const handleCategorySelect = (id: string): void => {
    setCurrentSelectedCategory(id);
    setCurrentSelectedSubCategory(null);
    const selectedCategory = categories.find((category) => category.id === id);
    if (selectedCategory && areSubCategoriesExist(selectedCategory)) {
      setCurrentSelection(null);
      setSubCategories(selectedCategory?.subcategories || []);
      return;
    }
    // if no subcategories then final selection is main-elements category
    setCurrentSelection(id);
    setSubCategories([]);
  };

  const handleSubCategorySelect = (id: string): void => {
    setCurrentSelectedSubCategory(id);
    setCurrentSelection(id);
  };

  const handleTagChange = (newSelectedTagsList: string[]): void => {
    setSelectedTags(newSelectedTagsList);
  };

  const redirect = useCallback((): void => {
    history.push({ pathname: Routes.REPORT });
  }, [history]);

  const getSimilarTransactions = useCallback(async () => {
    if (!transaction?.id) return;
    const data = await getSimilarTransactionsData(transaction.id);
    if (data) setNumberOfSimilar(data.similar);
  }, [transaction]);

  useEffect(() => {
    if (!transaction || !transaction?.type) redirect();
  }, [transaction, redirect]);

  useEffect(() => {
    getSimilarTransactions().then();
  }, [getSimilarTransactions]);

  return (
    <CustomDialog
      disableEnforceFocus
      open
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      data-test="report-change-category-modal"
    >
      <FsDialogTitle id="report-change-category-modal" onClose={handleClose}>
        {dialogTitle}
      </FsDialogTitle>

      <DialogContent dividers>
        <Box marginBottom={1}>
          <FsBody>{transactionLabel}</FsBody>
        </Box>
        {transaction && <TransactionDetails {...transaction} />}

        <Box marginBottom={2}>
          <FsBody>{categorySubTitle}</FsBody>
        </Box>

        <Box marginBottom={2}>
          <Divider />
        </Box>
        {(currentSelectedCategory || currentSelectedSubCategory) && (
          <HelperAlert
            currentSelection={currentSelection}
            currentSelectedCategory={currentSelectedCategory}
            currentSelectedSubCategory={currentSelectedSubCategory}
            data-test="category-helper-alert"
          />
        )}
        <Box marginBottom={2}>
          <FsGridContainer>
            <FsGridItem xs={12} sm={6}>
              {areCategories && (
                <CategoryList
                  caption={mainCategory}
                  categories={categories}
                  onSelect={handleCategorySelect}
                  currentSelection={currentSelectedCategory}
                  data-test="category-items-wrapper"
                />
              )}
            </FsGridItem>
            <FsGridItem xs={12} sm={6}>
              {areSubcategories && (
                <CategoryList
                  caption={subCategory}
                  categories={subCategories}
                  onSelect={handleSubCategorySelect}
                  currentSelection={currentSelectedSubCategory}
                  data-test="sub-category-items-wrapper"
                />
              )}
            </FsGridItem>
          </FsGridContainer>
        </Box>

        <Box marginBottom={2}>
          <Divider />
        </Box>

        <Box marginBottom={2}>
          <FsSubtitle>{tagTitle}</FsSubtitle>
          <FsBodySmall>{tagSubTitle}</FsBodySmall>
        </Box>
        <Box marginBottom={2}>
          <TagList tags={keyWords} onChange={handleTagChange} />
        </Box>
        {numberOfSimilar && (
          <>
            <Divider />
            <Box marginBottom={2} marginTop={2}>
              <FsBodySmall>
                {`${similarSubTitle[0]} `}
                <FsBodySmallBold component="span">
                  {numberOfSimilar}
                </FsBodySmallBold>{' '}
                {similarSubTitle[1]}
              </FsBodySmall>
              <Box>
                <FsCheckboxWithLabel
                  label={similarCheckboxLabel}
                  name={ExportFileType.PDF}
                  onChange={(e): void => setWithSimilar(e.target.checked)}
                  checked={withSimilar}
                />
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <FsButton
          onClick={handleClose}
          color="primary"
          data-test="cancel-button"
        >
          {cancelLabel}
        </FsButton>
        <FsPendingButton
          pending={pending}
          disabled={!currentSelection}
          data-test="save-button"
          onClick={handleSaveAndClose}
          startIcon={<SyncIcon />}
        >
          {saveChangesLabel}
        </FsPendingButton>
      </DialogActions>
    </CustomDialog>
  );
};

export default observer(ReportChangeCategoryModal);
